// NodeActivityFeed.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Hooks
import useNodeActivityFeed from 'node/activity/hooks/useNodeActivityFeed';

// Utilities
import {
  ACTIVITY_FEED_ITEM_KIND,
} from 'node/activity/utils/buildActivityFeedItems';
import { getActivityEventKey } from 'node/activity/utils/activityReactions';
import { groupActivityItemsByDay } from 'node/activity/utils/groupActivityItemsByDay';
import { buildActivityFieldDefLookup } from 'node/activity/utils/formatActivityFieldChange';
import { buildActivityFieldLabelLookup } from 'node/activity/utils/formatActivitySystemMessages';

// CSS
import './NodeActivityFeed.css';

// Components
import ActivityComposer from 'node/activity/composer/ActivityComposer';
import ActivityDetailCollapse from 'node/activity/items/ActivityDetailCollapse';
import ActivitySystemEvent from 'node/activity/items/ActivitySystemEvent';
import Comment from 'node/form/fields/comments/comment/Comment';
import Confirm from 'components/confirm/Confirm';

const NEAR_BOTTOM_PX = 80;

/**
 * NodeActivityFeed
 *
 * Chronological Activity surface: system events + comments, composer at bottom.
 *
 * @param {object|null} node - Target node.
 * @returns {ReactNode}
 */
const NodeActivityFeed = React.memo(({ node }) => {
  const listRef = useRef(null);
  const listInnerRef = useRef(null);
  const bottomRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const previousNodeKeyRef = useRef(null);
  const previousItemCountRef = useRef(0);

  const { getType } = useNodeTypes();
  const [draftAttachmentNodeKeys, setDraftAttachmentNodeKeys] = useState([]);
  const [expandedCollapses, setExpandedCollapses] = useState({});

  const {
    feedItems,
    comments,
    commentReactions,
    reactionsByEventKey,
    repliesByOriginalKey,
    showReplies,
    expandedReplies,
    loading,
    error,
    canComment,
    replyingTo,
    confirmDeleteOpen,
    setConfirmDeleteOpen,
    addComment,
    requestDeleteComment,
    confirmDeleteComment,
    toggleReplies,
    toggleMoreReplies,
    beginReply,
    cancelReply,
  } = useNodeActivityFeed(node, { draftAttachmentNodeKeys });

  const fieldLabels = useMemo(() => {
    const typeKey = typeof node?.type === 'string' ? node.type.trim() : '';
    const typeDef = typeKey ? (getType(typeKey) || null) : null;
    return buildActivityFieldLabelLookup(node, typeDef);
  }, [getType, node]);

  // Include getType so defs rebuild when the node-type catalog hydrates.
  const fieldDefs = useMemo(
    () => buildActivityFieldDefLookup(node),
    [getType, node]
  );

  const typeLabel = useMemo(() => {
    const typeKey = typeof node?.type === 'string' ? node.type.trim() : '';
    if (!typeKey) return '';
    return String(getType(typeKey)?.label || '').trim();
  }, [getType, node?.type]);

  const daySections = useMemo(
    () => groupActivityItemsByDay(feedItems),
    [feedItems]
  );

  const scrollToBottom = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
    bottomRef.current?.scrollIntoView({ block: 'end', inline: 'nearest' });
  }, []);

  const handleListScroll = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const distanceFromBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
    stickToBottomRef.current = distanceFromBottom <= NEAR_BOTTOM_PX;
  }, []);

  // Reset sticky-bottom whenever the node changes.
  useEffect(() => {
    const nodeKey = node?.key || null;
    if (previousNodeKeyRef.current === nodeKey) return;
    previousNodeKeyRef.current = nodeKey;
    previousItemCountRef.current = 0;
    stickToBottomRef.current = true;
    setExpandedCollapses({});
  }, [node?.key]);

  // Keep the live edge in view on open / growth / late image layout.
  useEffect(() => {
    const itemCount = feedItems.length;
    const grew = itemCount > previousItemCountRef.current;
    previousItemCountRef.current = itemCount;

    if (grew) {
      stickToBottomRef.current = true;
    }

    if (!stickToBottomRef.current) return undefined;

    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (!stickToBottomRef.current) return;
        scrollToBottom();
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [feedItems, loading, scrollToBottom]);

  useEffect(() => {
    const list = listRef.current;
    const inner = listInnerRef.current;
    if (!list || !inner || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      if (!stickToBottomRef.current) return;
      scrollToBottom();
    });

    observer.observe(inner);
    return () => observer.disconnect();
  }, [scrollToBottom, node?.key]);

  const handleComposerSubmit = async (text, attachments) => {
    stickToBottomRef.current = true;
    return addComment(text, replyingTo, attachments);
  };

  const handleDraftAttachmentKeysChange = useCallback((keys) => {
    setDraftAttachmentNodeKeys(Array.isArray(keys) ? keys : []);
  }, []);

  if (!node) return null;

  return (
    <div className="node-activity-feed-container">
      <div
        className="node-activity-feed-list"
        ref={listRef}
        onScroll={handleListScroll}
      >
        <div
          className="node-activity-feed-list-inner"
          ref={listInnerRef}
        >
          {loading && feedItems.length === 0 ? (
            <div className="node-activity-feed-empty">Loading activity…</div>
          ) : null}

          {!loading && error && feedItems.length === 0 ? (
            <div className="node-activity-feed-empty">{error}</div>
          ) : null}

          {!loading && !error && feedItems.length === 0 ? (
            <div className="node-activity-feed-empty">
              No activity yet. Leave the first comment below.
            </div>
          ) : null}

          {daySections.map((section) => {
            const timestampStyle = section.dateLabel ? 'time' : 'datetime';

            return (
              <div
                key={section.key}
                className="node-activity-feed-day"
              >
                {section.dateLabel ? (
                  <div className="node-activity-feed-day-label">
                    {section.dateLabel}
                  </div>
                ) : null}

                <div className="node-activity-feed-day-rows">
                  {section.items.map((item) => {
                    if (item.kind === ACTIVITY_FEED_ITEM_KIND.COLLAPSE) {
                      return (
                        <ActivityDetailCollapse
                          key={item.key}
                          item={item}
                          expanded={expandedCollapses[item.key] === true}
                          fieldDefs={fieldDefs}
                          fieldLabels={fieldLabels}
                          node={node}
                          reactionsByEventKey={reactionsByEventKey}
                          timestampStyle={timestampStyle}
                          typeLabel={typeLabel}
                          onToggle={() => {
                            setExpandedCollapses((current) => ({
                              ...current,
                              [item.key]: current[item.key] !== true,
                            }));
                          }}
                        />
                      );
                    }

                    if (item.kind === ACTIVITY_FEED_ITEM_KIND.SYSTEM) {
                      return (
                        <ActivitySystemEvent
                          key={item.key}
                          event={item.event}
                          fieldDefs={fieldDefs}
                          fieldLabels={fieldLabels}
                          node={node}
                          reactions={reactionsByEventKey.get(getActivityEventKey(item.event)) || []}
                          timestampStyle={timestampStyle}
                          typeLabel={typeLabel}
                        />
                      );
                    }

                    if (item.kind === ACTIVITY_FEED_ITEM_KIND.COMMENT) {
                      const comment = item.comment;
                      const replies = repliesByOriginalKey.get(comment.key) || [];
                      const displayedReplies = expandedReplies[comment.key]
                        ? replies
                        : replies.slice(0, 3);
                      const hasMoreReplies = replies.length > displayedReplies.length;

                      return (
                        <Comment
                          key={item.key}
                          node={node}
                          comment={comment}
                          comments={comments}
                          reactions={commentReactions}
                          replies={replies}
                          repliesCount={replies.length}
                          hasMoreReplies={hasMoreReplies}
                          showReplies={showReplies}
                          displayedReplies={displayedReplies}
                          timestampStyle={timestampStyle}
                          onDeleteClicked={requestDeleteComment}
                          onReplyClicked={(event, target) => {
                            event?.stopPropagation?.();
                            beginReply(target);
                          }}
                          onToggleMoreReplies={toggleMoreReplies}
                          onToggleReplies={toggleReplies}
                        />
                      );
                    }

                    return null;
                  })}
                </div>
              </div>
            );
          })}
          <div
            className="node-activity-feed-list-end"
            ref={bottomRef}
            aria-hidden="true"
          />
        </div>
      </div>

      <div className="node-activity-feed-composer">
        <ActivityComposer
          node={node}
          canComment={canComment}
          replyingTo={replyingTo}
          onSubmit={handleComposerSubmit}
          onCancelReply={cancelReply}
          onDraftAttachmentKeysChange={handleDraftAttachmentKeysChange}
        />
      </div>

      <Confirm
        title="Confirm Delete"
        message="Are you sure you want to delete this comment?"
        isOpen={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={confirmDeleteComment}
      />
    </div>
  );
});

NodeActivityFeed.displayName = 'NodeActivityFeed';

export default NodeActivityFeed;
